import {
  Divider,
  FormikInput,
  Link,
  LoadingButton,
  numberUtils,
  Stack,
  styled,
  Typography,
  useResponsive,
  useTheme
} from '@rabex-kit/rabex-ui';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';
import useInAppLink from '@/hooks/useInAppLink';
import { FORGET, REGISTER } from '../../pages/routes';
import { LoginRequestBody } from '../../services/auth/types';
import LoginWithGoogle from '../../containers/Login/google';
import { ClearButton, PasswordToggle } from '../AuthIconButton';

// Anchored: the whole value has to be an address, not just contain one. Callers trim first.
export const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const mobileRegex = /^09\d{9}$/;

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  ...theme.typography.body2,
  '& > :not(style) ~ :not(style)': {
    marginTop: theme.spacing(2)
  }
}));

export type LoginProps = {
  isLoading: boolean;
  onSubmit?: (values: LoginRequestBody) => Promise<void>;
};

const LoginForm: React.FC<LoginProps> = ({ onSubmit, isLoading }) => {
  const inAppLink = useInAppLink();
  const { matches } = useResponsive();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (
    values: LoginRequestBody,
    formikHelpers: FormikHelpers<LoginRequestBody>
  ) => {
    if (values.phoneNumber && values.password) {
      if (onSubmit) {
        try {
          // Validation already reads the number in ASCII digits; send it that way too.
          await onSubmit({
            ...values,
            phoneNumber: numberUtils.toEn(values.phoneNumber).trim()
          });
        } catch (error: any) {
          if (error?.data?.message) {
            formikHelpers.setFieldError('password', error.data.message); // Displaying error under password input
            // formikHelpers.setFieldError('phoneNumber', error.data.message); // Displaying error under phoneNumber input
          } else {
            // Generic error if message is not available
            formikHelpers.setErrors({
              phoneNumber:
                'ورود ناموفق. لطفا ایمیل یا شماره تلفن خود را چک کنید',
              password: 'ورود ناموفق. لطفا رمز عبور خود را چک کنید'
            });
          }
        }
      }
    }
  };

  const validationSchema = yup.object({
    phoneNumber: yup
      .string()
      .required('ایمیل یا شماره همراه الزامی است.')
      .transform((value) => numberUtils.toEn(value))
      .test(
        'valid-email-or-mobile',
        'لطفاً یک ایمیل معتبر یا شماره همراه وارد کنید.',
        (value) => {
          if (!value) return false;
          return (
            emailRegex.test(value.trim()) || mobileRegex.test(value.trim())
          );
        }
      ),
    password: yup
      .string()
      .required('رمز عبور الزامی است.')
      .min(8, 'رمزعبور حداقل باید ۸ رقم باشد')
  });

  return (
    <Stack
      gap={0}
      width={{ xs: '100%', lg: 464 }}
      // mt={{ lg: 5, xs: 0 }}
      zIndex={20}>
      <Formik
        initialValues={{
          phoneNumber: '',
          password: ''
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}>
        {(formik) => (
          <Form>
            <Stack gap={4} sx={{ mb: 6 }}>
              <FormikInput
                title="برای ورود شماره موبایل یا ایمیل خود را وارد کنید."
                size={matches({ xs: 'md', lg: 'lg' })}
                placeholder="شماره همراه یا ایمیل"
                fill={theme.palette.mode === 'light'}
                name="phoneNumber"
                endAdornment={
                  formik.values.phoneNumber && (
                    <ClearButton
                      onClick={() => formik.setFieldValue('phoneNumber', '')}
                    />
                  )
                }
              />

              <FormikInput
                value={formik.values.password}
                size={matches({ xs: 'md', lg: 'lg' })}
                fill={theme.palette.mode === 'light'}
                name="password"
                placeholder="رمز عبور"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <>
                    {formik.values.password && (
                      <ClearButton
                        onClick={() => {
                          formik.setFieldValue('password', '');
                        }}
                      />
                    )}
                    <PasswordToggle
                      shown={showPassword}
                      onToggle={() => setShowPassword(!showPassword)}
                    />
                  </>
                }
              />
              <Link
                {...inAppLink(FORGET)}
                fontWeight="600"
                color="textColor.A80"
                sx={{ textDecoration: 'none' }}
                variant={matches({ xs: 'body3', lg: 'body2' })}>
                رمز عبور خود را فراموش کرده‌اید؟
              </Link>
            </Stack>

            <LoadingButton
              size={matches({ xs: 'md', lg: 'lg' })}
              loading={isLoading}
              disabled={
                formik.values.phoneNumber === '' ||
                formik.values.password === ''
              }
              type="submit"
              fullWidth
              color="primary">
              ورود به حساب کاربری
            </LoadingButton>
            <Stack
              sx={{ my: matches({ lg: '24px', xs: '8px' }) }}
              direction="row"
              gap="12px">
              <Root>
                <Divider>
                  <Typography sx={{ px: 2 }}>یا</Typography>
                </Divider>
              </Root>
            </Stack>
            <Stack alignItems="center">
              <LoginWithGoogle text="signin_with" />
            </Stack>
          </Form>
        )}
      </Formik>
      <Link
        mt={matches({ lg: 6, xs: 4 })}
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
        {...inAppLink(REGISTER)}
        fontWeight="400"
        color="textColor.A80"
        sx={{ textDecoration: 'none' }}
        variant={matches({ xs: 'body3', lg: 'body2' })}>
        حساب کاربری ندارید؟
        <Typography
          weight="semiBold"
          variant={matches({ lg: 'body2', xs: 'body3' })}
          color={theme.palette.primary[500]}>
          ثبت‌نام کنید
        </Typography>
      </Link>
    </Stack>
  );
};

export default LoginForm;
